import { useCallback, useMemo } from 'react'

import { GroupingDefinition } from '@factorialco/f0-react'
import { ItAssetsTableQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  ASSET_STATUS,
  AssetStatus,
  AssetType,
  isValidAssetGroup,
  isValidAssetStatus,
  isValidAssetType,
} from 'modules/it_management/components/AssetsList/utils'
import type { AssetGroup } from 'modules/it_management/components/AssetsList/utils'

type ItAsset = ItAssetsTableQuery['itManagement']['itAssetsConnection']['nodes'][number]

const getGroupTranslation = (groupId: AssetGroup): string => {
  const translations: Record<AssetGroup, string> = {
    // Status groups
    in_stock: i18n.t('it_management.assets.status.in_stock'),
    assigned: i18n.t('it_management.assets.status.assigned'),
    maintenance: i18n.t('it_management.assets.status.maintenance'),
    retired: i18n.t('it_management.assets.status.retired'),

    // Type groups
    laptop: i18n.t('it_management.assets.type.laptop'),
    phone: i18n.t('it_management.assets.type.phone'),
    accessory: i18n.t('it_management.assets.type.accessory'),
    screen: i18n.t('it_management.assets.type.screen'),
    mouse: i18n.t('it_management.assets.type.mouse'),
    keyboard: i18n.t('it_management.assets.type.keyboard'),
    headset: i18n.t('it_management.assets.type.headset'),
    other: i18n.t('it_management.assets.type.other'),
  }

  return translations[groupId] ?? groupId
}

type GroupingStrategy = 'status' | 'type'

type Params = {
  groupingStrategy: GroupingStrategy
  statuses?: string[]
  typeNames?: string[]
}

export const useGrouping = ({
  groupingStrategy,
  statuses: _statuses,
  typeNames: _typeNames,
}: Params) => {
  const _assetGrouping = undefined

  const getStatusGroup = useCallback((item: ItAsset): AssetStatus => {
    const normalized = item.status?.toLowerCase().replace(/\s+/g, '_') ?? ASSET_STATUS.IN_STOCK
    return isValidAssetStatus(normalized) ? normalized : ASSET_STATUS.IN_STOCK
  }, [])

  const getTypeGroup = useCallback((item: ItAsset): AssetType => {
    const normalized = item.itAssetModel?.typeName?.toLowerCase() ?? 'other'
    return isValidAssetType(normalized) ? normalized : 'other'
  }, [])

  const mapData = useCallback(
    (data: ItAssetsTableQuery) => {
      const nodes = data.itManagement.itAssetsConnection.nodes

      return nodes.map((node) => {
        let group: AssetGroup

        switch (groupingStrategy) {
          case 'status':
            group = getStatusGroup(node)
            break
          case 'type':
            group = getTypeGroup(node)
            break
          default:
            group = getStatusGroup(node)
        }

        return {
          ...node,
          asset_type: getTypeGroup(node),
          group,
        }
      })
    },
    [groupingStrategy, getStatusGroup, getTypeGroup]
  )

  const grouping = useMemo<GroupingDefinition<ItAsset & { group: AssetGroup }>>(
    () => ({
      collapsible: true,
      defaultOpenGroups: true,
      groupBy: {
        status: {
          name: i18n.t('it_management.assets.grouping.status'),
          label: (groupId: string) => {
            if (isValidAssetGroup(groupId)) {
              return getGroupTranslation(groupId)
            }
            return getGroupTranslation('in_stock') // fallback
          },
          itemCount: (_groupValue: string) => {
            // For now, use client-side counting (return undefined to let the component count)
            // In a full implementation, you'd return server-side counts from GraphQL
            return undefined
          },
        },
        asset_type: {
          name: i18n.t('it_management.assets.grouping.asset_type'),
          label: (assetType: string) => {
            // Use type guard for cleaner code
            if (isValidAssetGroup(assetType)) {
              return getGroupTranslation(assetType)
            }
            return getGroupTranslation('laptop') // fallback
          },
          itemCount: (_assetType: string) => {
            return undefined
          },
        },
      },
    }),
    []
  )

  return {
    mapData,
    grouping,
    groupingStrategy,
  }
}
